/**
 * SingleBlock component to handle Ultamate Rewards utility flyout
 *
 * @module views/components/SingleBlock
 * @memberof -Common
 */
import './SingleBlock.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';

import { Link } from '../Link/Link';

/**
 * Represents a SingleBlock component
 *
 * @method
 * @param {SingleBlockProps} props - React properties passed from composition
 * @returns SingleBlock
 */
export const SingleBlock = function( props ){
  const { eyebrow, headline, description, buttonAction, linkAction1, linkAction2 } = props;

  if( !eyebrow && !headline && !description && !buttonAction && !linkAction1 && !linkAction2 ){
    return null;
  }

  return (
    <div className='SingleBlock'>
      { eyebrow && (
        <div className='SingleBlock__eyebrow'>
          <Text textStyle='eyebrow'
            color='neutral-600'
          >
            { eyebrow }
          </Text>
        </div>
      ) }

      { headline && (
        <div className='SingleBlock__headline'>
          <Text textStyle='title-6'
            htmlTag='h2'
          >
            { headline }
          </Text>
        </div>
      ) }

      { description && (
        <div className='SingleBlock__description'>
          <Text textStyle='body-2'
            color='neutral-600'
          >
            { description }
          </Text>
        </div>
      ) }

      { buttonAction?.label && buttonAction?.navigationType !== DXL_NAVIGATION_TYPE.Redirect && (
        <div className='SingleBlock__action'>
          <Button
            type='submit'
            className='SingleBlock__submit'
            fullWidth={ true }
            ariaLabel={ buttonAction.label }
            label={ buttonAction.label }
          />
        </div>
      ) }

      { buttonAction?.label && buttonAction?.navigationType === DXL_NAVIGATION_TYPE.Redirect && buttonAction?.url && (
        <div className='SingleBlock__action'>
          <Link_Huge likeButtonPrimary
            url={ buttonAction.url }
            action={ buttonAction }
          >
            { buttonAction.label }
          </Link_Huge>
        </div>
      ) }

      { ( linkAction1?.label || linkAction2?.label ) && (
        <ul className='SingleBlock__actionsArrowLink'>
          { linkAction1?.label && <li>{ <SingleBlockAction action={ linkAction1 } /> }</li> }
          { linkAction2?.label && <li>{ <SingleBlockAction action={ linkAction2 } /> }</li> }
        </ul>
      ) }
    </div>
  );
};

/**
 * SingleBlockAction method returns Button or Link based on the action object is passed as a props
 * @param {object} action - - object contains variables like label, navigationType and graphql
 */
export const SingleBlockAction = ( props ) => {
  const { action } = props;
  const isButton = ( action?.navigationType && action.graphql );

  if( !action.label ){
    return null;
  }

  if( !isButton ){
    return (
      <Link
        linkAction={ action }
        linkIcon='ArrowForward'
        linkStyle='body-2'
        ariaLabel={ action.label }
        iconsize='s'
      />
    );
  }

  return (
    <Button
      likeLink
      ariaLabel={ action.label }
      action={ action }
      className='SingleBlock__actionsArrowButton'
      crossButtonVisibility={ true }
    >
      <Text
        textStyle='body-2'
        color='black'
      >
        { action.label }
        <Icon size='s'
          name='ArrowForward'
        />
      </Text>
    </Button>
  );
};

/**
 * Property type definitions
 * @typedef SingleBlockProps
 * @type {object}
 * @property {string} eyebrow - Sets value for eyebrow
 * @property {string} headline - Sets value for headline
 * @property {string} description - Sets value for description
 * @property {object} buttonAction - Sets the buttonAction properties
 * @property {object} linkAction1 - Sets the linkAction1 properties
 * @property {object} linkAction2 - Sets the linkAction2 properties
 */
export const propTypes =  {
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  description: PropTypes.string,
  buttonAction: PropTypes.object,
  linkAction1: PropTypes.object,
  linkAction2: PropTypes.object
};

SingleBlock.propTypes = propTypes;

export default SingleBlock;

